import { useEffect, useRef, useState } from 'react';

import { Siderbar } from './Sidebar';
import { FAQ } from './sections/FAQ';
import { Overview } from './sections/Overview';
import { WorkerClassificationSelection } from './sections/WorkerClassificationSelection';

const useReferences = () => {
  const referencesByKey = useRef({});

  const setReference = (element, key) => {
    referencesByKey.current[key] = element;
  };

  return { refsByKey: referencesByKey.current, setRef: setReference };
};

/**
 * @param office - the office the page describes and saves to
 * @param viewedOfficeId - set when that office is a location other than the selected one
 */
export const Content = ({ isRegistration, office, viewedOfficeId }) => {
  const { refsByKey, setRef } = useReferences();
  const userClassification = office?.workerClassification?.classificationType;
  const latestWorkerClassificationRequest = office?.latestWorkerClassificationRequest;
  const workerClassificationRequest = office?.workerClassificationRequest;
  const [activeSection, setActiveSection] = useState(null);
  const isPendingWCRequest =
    latestWorkerClassificationRequest?.status === 'requested' ||
    (workerClassificationRequest?.length === 1 &&
      workerClassificationRequest?.[0]?.status === 'requested');

  const handleScroll = () => {
    const sectionKeys = Object.keys(refsByKey);
    let activeSectionKey = null;
    const maxScroll = document.body.scrollHeight - window.innerHeight;

    sectionKeys.forEach((key, index) => {
      const currentRef = refsByKey[key];

      const nextRef = refsByKey[sectionKeys[index + 1]];

      const currentTop = currentRef ? currentRef.offsetTop - 200 : 0;

      const nextTop = nextRef ? nextRef.offsetTop : Number.POSITIVE_INFINITY;

      const scrollPosition = window.scrollY;

      if (scrollPosition >= currentTop && scrollPosition < nextTop) {
        activeSectionKey = key;
      }

      if (scrollPosition >= maxScroll - 100) {
        activeSectionKey = sectionKeys[sectionKeys.length - 1];
      }
    });
    setActiveSection(activeSectionKey);
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedHandleScroll = debounce(handleScroll, 50);

  useEffect(() => {
    window.addEventListener('scroll', debouncedHandleScroll);
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        marginLeft: 20,
        height: '100%',
        ...(isRegistration && { marginLeft: 0 }),
      }}
    >
      <Siderbar
        isRegistration={isRegistration}
        refsByKey={refsByKey}
        activeSection={activeSection}
        office={office}
      />

      <div
        style={{
          width: 'calc(100% - 400px)',
          marginLeft: 80,
          marginRight: 50,
          marginBottom: 120,
        }}
      >
        <div ref={(element) => setRef(element, 'overview')}>
          <Overview />
        </div>
        <div ref={(element) => setRef(element, 'faqs')}>
          <FAQ isRegistration={isRegistration} showDescription={!userClassification} />
        </div>

        {!isPendingWCRequest && !userClassification && (
          <div ref={(element) => setRef(element, 'worker-classification-selection')}>
            <WorkerClassificationSelection
              isRegistration={isRegistration}
              office={office}
              viewedOfficeId={viewedOfficeId}
            />
          </div>
        )}
      </div>
    </div>
  );
};
